// Bejelentkezés — a Claude Design "Atlas" terv login-képernyője (docs/foundations/design.md, logika: docs/foundations/auth.md).
// A VIZUÁLIS BUROK a közös `AuthShell` (bal ≥lg: sötét vizuál-panel rács-háttérrel + MORGENS-zöld derengéssel,
// headline + statisztikák; mobilon csak a tartalom, logóval) — ugyanaz, amit az „Elfelejtett jelszó" és a
// „Jelszó beállítása" oldal használ. Itt CSAK a login-űrlap él: email/jelszó, „Emlékezz rám", near-black CTA.
// Odin iam login (apps/api BFF proxy) → access-token a store-ba (memória) + refresh httpOnly cookie-ba.
import { LoginBodySchema } from '@atlas/contracts';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { AuthShell } from '@/components/auth/auth-shell';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/components/ui/password-input';
import { api } from '@/lib/api';
import { useAuthStore } from '@/lib/auth-store';

export const Route = createFileRoute('/login')({
  component: LoginPage,
});

type LoginValues = z.infer<typeof LoginBodySchema>;

function LoginPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setToken = useAuthStore((s) => s.setToken);
  const [rememberMe, setRememberMe] = useState(true);
  // 2FA: a user kétlépcsős → a 401 `mfa_required`-re kód-mezőt nyitunk; `mfa_invalid` → mező-hiba.
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaError, setMfaError] = useState<string | null>(null);
  const login = api.login.useMutation();

  /**
   * A resolver-séma a KONTRAKTBÓL származik, de i18n-ÜZENETEKKEL. MIÉRT: a contract-séma nyelv-független
   * (szerver-oldali validáció), tehát a Zod ANGOL defaultjait adta vissza a felületnek („Invalid email",
   * „String must contain at least 1 character(s)") — magyar felületen is angolul. A mező-shape marad a
   * contractból (egy igazság-forrás), csak a szövegeket írjuk felül.
   */
  const formSchema = useMemo(
    () =>
      LoginBodySchema.extend({
        email: z
          .string()
          .min(1, t('validation:auth.emailRequired'))
          .email(t('validation:auth.emailInvalid')),
        password: z.string().min(1, t('validation:auth.passwordRequired')),
      }),
    [t],
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { email: '', password: '', mfaCode: '' },
  });

  const onSubmit = handleSubmit((values) => {
    setMfaError(null);
    login.mutate(
      { body: { ...values, rememberMe } },
      {
        onSuccess: (res) => {
          if (res.status === 200) {
            setToken(res.body.accessToken);
            // A FELÜLET-VÁLASZTÁS a belépés UTÁN, a token TÍPUSÁBÓL (user-döntés: egy belépő-
            // felület, egy cím). A `/session` a friss tokennel válaszol, és a `type` mondja meg, hová megyünk:
            // `partner` → a portál, `internal` → a belső felület. Ha a session-hívás bármiért nem sikerül, a
            // BELSŐ felületre visszük: ott a route-guard és a BFF falai amúgy is helyre teszik a jogosultságot,
            // míg a portálra terelt belsős csak egy értelmezhetetlen üres lapot kapna.
            void api.session
              .query({})
              .then((s) =>
                navigate({
                  to: s.status === 200 && s.body.type === 'partner' ? '/portal' : '/',
                }),
              )
              .catch(() => navigate({ to: '/' }));
          }
        },
        // ts-rest: a nem-2xx (a contractban definiált 401 is) onError-ra fut — itt bontunk státusz + `code` szerint.
        onError: (err) => {
          const status = (err as { status?: number } | null)?.status;
          const code = (err as { body?: { code?: string } } | null)?.body?.code;
          if (status === 401 && code === 'mfa_required') {
            // A jelszó helyes, de 2FA kell → kód-mezőt mutatunk (nincs „hibás jelszó" félrevezetés).
            setMfaRequired(true);
            return;
          }
          if (status === 401 && code === 'mfa_invalid') {
            setMfaRequired(true);
            setMfaError(t('login.mfaInvalid'));
            return;
          }
          // A HIBA-OKOK SZÉTVÁLASZTVA. Csak a valódi 401 mondja azt, hogy a jelszó rossz — a rate-limit és az
          // elérhetetlen auth-szolgáltatás SAJÁT üzenetet kap. (Korábban a BFF minden hibát 401-re képezett, így
          // egy leállt platform is „hibás jelszót" jelentett: a felhasználó a saját jelszavát kezdte keresni.)
          if (status === 429) {
            toast.error(t('login.errThrottled'));
            return;
          }
          if (status === 503) {
            toast.error(t('login.errUnavailable'));
            return;
          }
          // A jelszó JÓ, de nincs Atlas-hozzáférés (nem app-tag) — külön üzenet: a felhasználó ne a jelszavát
          // keresse, és ne is „átmeneti hibára" várjon, hanem jogosultságot kérjen.
          if (status === 403) {
            toast.error(t('login.errNoAccess'));
            return;
          }
          // Minden más 401 → GENERIKUS üzenet (nincs user-enumeration / jog-szivárgás).
          toast.error(t(status === 401 ? 'login.errInvalid' : 'login.errFailed'));
        },
      },
    );
  });

  return (
    <AuthShell
      title={t('login.title')}
      subtitle={t('login.subtitle')}
      // NINCS „hozzáférés kérése" lábjegyzet: ez a BELSŐS munkatársak belépő felülete, a fiókot az
      // adminisztrátor hozza létre (Odin `iam`). A külsős (partner) belépés MÁS felületen lesz, ha lesz —
      // egy nem-működő „Kérj hozzáférést" link itt csak hamis ígéret volt (`comingSoon` tooltippel).
    >
      <form onSubmit={onSubmit}>
        <div className="mb-3.5 space-y-1.5">
          <Label htmlFor="email" className="text-sm font-medium">
            {t('login.email')}
          </Label>
          <Input
            id="email"
            type="email"
            autoComplete="username"
            placeholder="nev@morgens.hu"
            className="h-[38px]"
            {...register('email')}
          />
          {errors.email && <p className="text-destructive text-xs">{errors.email.message}</p>}
        </div>
        <div className="mb-3.5 space-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="password" className="text-sm font-medium">
              {t('login.password')}
            </Label>
            {/* Belépő az „Elfelejtett jelszó" folyamatba (a levélbeli linkkel folytatódik). */}
            <Link
              to="/forgot-password"
              className="text-muted-foreground hover:text-foreground text-xs hover:underline"
            >
              {t('login.forgot')}
            </Link>
          </div>
          <PasswordInput
            id="password"
            autoComplete="current-password"
            className="h-[38px]"
            {...register('password')}
          />
          {errors.password && <p className="text-destructive text-xs">{errors.password.message}</p>}
        </div>

        {/* 2FA kód-mező — CSAK ha a szerver `mfa_required`/`mfa_invalid`-ot jelzett (a jelszó már helyes).
            TOTP (6 jegy) VAGY recovery-kód is beírható. */}
        {mfaRequired && (
          <div className="animate-in fade-in slide-in-from-top-1 mb-3.5 space-y-1.5 duration-200">
            <Label htmlFor="mfaCode" className="text-sm font-medium">
              {t('login.mfaLabel')}
            </Label>
            <Input
              id="mfaCode"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder={t('login.mfaPlaceholder')}
              className="h-[38px]"
              autoFocus
              {...register('mfaCode')}
            />
            <p className="text-muted-foreground text-xs">{t('login.mfaHint')}</p>
            {mfaError && <p className="text-destructive text-xs">{mfaError}</p>}
          </div>
        )}

        {/* „Emlékezz rám" (iparági minta): pipa → perzisztens session (30 nap, csúszó ablak);
            nincs pipa → a böngésző bezárásával lejár. */}
        <label className="text-md mb-5 flex cursor-pointer items-center gap-2">
          <Checkbox checked={rememberMe} onCheckedChange={(v) => setRememberMe(v === true)} />
          {t('login.remember')}
        </label>

        <Button
          type="submit"
          variant="brand"
          size="lg"
          className="w-full"
          disabled={login.isPending}
        >
          {login.isPending ? (
            t('login.submitting')
          ) : (
            <>
              {t('login.submit')} <ArrowRightIcon />
            </>
          )}
        </Button>
      </form>
    </AuthShell>
  );
}
